export type GoogleMapsAutocompleteSessionToken = object;

export type GoogleMapsAddressComponent = { longText?: string; shortText?: string; types: string[] };
export type GoogleMapsPlace = {
  id?: string;
  formattedAddress?: string;
  displayName?: string;
  addressComponents?: GoogleMapsAddressComponent[];
  location?: {
    lat: () => number;
    lng: () => number;
  } | null;
  fetchFields: (options: { fields: string[] }) => Promise<void>;
};

export type GoogleMapsFormattableText = {
  text?: string;
  toString?: () => string;
};

export type GoogleMapsPlacePrediction = {
  placeId: string;
  text: GoogleMapsFormattableText;
  mainText?: GoogleMapsFormattableText | null;
  secondaryText?: GoogleMapsFormattableText | null;
  structuredFormat?: {
    mainText?: GoogleMapsFormattableText | null;
    secondaryText?: GoogleMapsFormattableText | null;
  } | null;
  toPlace: () => GoogleMapsPlace;
};

export type GoogleMapsPlacesLibrary = {
  AutocompleteSessionToken: new () => GoogleMapsAutocompleteSessionToken;
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions: (request: {
      input: string;
      sessionToken: GoogleMapsAutocompleteSessionToken;
      language?: string;
      region?: string;
    }) => Promise<{
      suggestions: { placePrediction?: GoogleMapsPlacePrediction | null }[];
    }>;
  };
};

export type GoogleMapsLatLngLiteral = {
  lat: number;
  lng: number;
};

export type GoogleMapsMap = {
  setCenter: (position: GoogleMapsLatLngLiteral) => void;
  getCenter: () => { lat: () => number; lng: () => number } | undefined;
  addListener: (name: string, callback: (event: { latLng?: { lat: () => number; lng: () => number } }) => void) => { remove: () => void };
  fitBounds: (bounds: GoogleMapsLatLngBounds, padding?: number | Record<string, number>) => void;
  getZoom: () => number | undefined;
  setZoom: (zoom: number) => void;
};

export type GoogleMapsLatLngBounds = {
  extend: (position: GoogleMapsLatLngLiteral) => GoogleMapsLatLngBounds;
};

export type GoogleMapsOverlay = {
  draw?: () => void;
  getPanes: () => { overlayMouseTarget: HTMLElement } | null;
  getProjection: () => {
    fromLatLngToDivPixel: (position: unknown) => { x: number; y: number } | null;
  } | null;
  onAdd?: () => void;
  onRemove?: () => void;
  setMap: (map: GoogleMapsMap | null) => void;
};

export type GoogleMapsPolyline = {
  setMap: (map: GoogleMapsMap | null) => void;
};

export type GoogleMapsLibrary = {
  Map: new (element: HTMLElement, options: Record<string, unknown>) => GoogleMapsMap;
  LatLng: new (latitude: number, longitude: number) => unknown;
  LatLngBounds: new () => GoogleMapsLatLngBounds;
  OverlayView: new () => GoogleMapsOverlay;
  Polyline: new (options: Record<string, unknown>) => GoogleMapsPolyline;
  event: {
    trigger: (instance: GoogleMapsMap, eventName: string) => void;
  };
  geometry?: {
    encoding?: {
      decodePath: (encodedPath: string) => unknown[];
    };
  };
};

type GoogleMapsWindow = Window & {
  google?: {
    maps?: {
      importLibrary?: (library: string) => Promise<unknown>;
    };
  };
};

const SCRIPT_ID = "brandwise-google-maps-script";
const CALLBACK_NAME = "__brandwiseGoogleMapsReady";
let scriptPromise: Promise<void> | null = null;
let placesLibraryPromise: Promise<GoogleMapsPlacesLibrary> | null = null;
let mapsLibraryPromise: Promise<GoogleMapsLibrary> | null = null;
let loadedApiKey: string | null = null;

export function loadGooglePlacesLibrary(apiKey: string) {
  const normalizedKey = validateApiKey(apiKey);
  if (normalizedKey instanceof Error) return Promise.reject(normalizedKey);
  if (placesLibraryPromise && loadedApiKey === normalizedKey) {
    return placesLibraryPromise;
  }
  if (loadedApiKey && loadedApiKey !== normalizedKey) {
    return Promise.reject(new Error("Google Maps was already loaded with a different browser key."));
  }
  loadedApiKey = normalizedKey;
  placesLibraryPromise = loadGoogleMapsScript(normalizedKey).then(importPlacesLibrary).catch(error => { placesLibraryPromise = null; throw error; });
  return placesLibraryPromise;
}

export function loadGoogleMapsLibrary(apiKey: string) {
  const normalizedKey = validateApiKey(apiKey);
  if (normalizedKey instanceof Error) return Promise.reject(normalizedKey);
  if (mapsLibraryPromise && loadedApiKey === normalizedKey) {
    return mapsLibraryPromise;
  }
  if (loadedApiKey && loadedApiKey !== normalizedKey) {
    return Promise.reject(new Error("Google Maps was already loaded with a different browser key."));
  }
  loadedApiKey = normalizedKey;
  mapsLibraryPromise = loadGoogleMapsScript(normalizedKey).then(importMapsLibrary).catch(error => { mapsLibraryPromise = null; throw error; });
  return mapsLibraryPromise;
}

function validateApiKey(apiKey: string) {
  const normalizedKey = apiKey.trim();
  return normalizedKey || new Error("Google Maps browser key is not configured.");
}

async function importPlacesLibrary() {
  const maps = (window as GoogleMapsWindow).google?.maps;
  if (!maps?.importLibrary) {
    throw new Error("Google Maps JavaScript API did not initialize.");
  }
  return maps.importLibrary("places") as Promise<GoogleMapsPlacesLibrary>;
}

async function importMapsLibrary() {
  const maps = (window as GoogleMapsWindow).google?.maps;
  if (!maps?.importLibrary) {
    throw new Error("Google Maps JavaScript API did not initialize.");
  }
  await Promise.all([
    maps.importLibrary("maps"),
    maps.importLibrary("core"),
    maps.importLibrary("geometry")
  ]);
  const loadedMaps = (window as GoogleMapsWindow).google?.maps as unknown as GoogleMapsLibrary | undefined;
  if (!loadedMaps?.Map || !loadedMaps.LatLngBounds || !loadedMaps.OverlayView || !loadedMaps.Polyline) {
    throw new Error("Google Maps map library did not initialize.");
  }
  return loadedMaps;
}

function loadGoogleMapsScript(apiKey: string) {
  // With loading=async, the script's load event can precede API initialization.
  // All library requests must share the readiness callback, including retries.
  if (scriptPromise) return scriptPromise;
  if ((window as GoogleMapsWindow).google?.maps?.importLibrary) {
    return Promise.resolve();
  }
  const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const globals = window as unknown as Record<string, unknown>;
    const script = existingScript ?? document.createElement("script");
    const previousCallback = globals[CALLBACK_NAME];
    let settled = false;
    const cleanup = () => {
      window.clearTimeout(timeout);
      script.removeEventListener("error", onError);
      if (globals[CALLBACK_NAME] === onReady) delete globals[CALLBACK_NAME];
    };
    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      script.remove();
      reject(new Error(message));
    };
    const onError = () => fail("Google Maps JavaScript API could not be loaded.");
    const onReady = () => {
      if (settled) return;
      settled = true;
      cleanup();
      try {
        if (typeof previousCallback === "function") previousCallback();
      } finally {
        resolve();
      }
    };
    const timeout = window.setTimeout(() => fail("Google Maps initialization timed out. Please try again."), 20000);
    globals[CALLBACK_NAME] = onReady;
    script.addEventListener("error", onError, { once: true });
    if (existingScript) return;

    const url = new URL("https://maps.googleapis.com/maps/api/js");
    url.searchParams.set("key", apiKey);
    url.searchParams.set("v", "weekly");
    url.searchParams.set("libraries", "places");
    url.searchParams.set("loading", "async");
    url.searchParams.set("auth_referrer_policy", "origin");
    url.searchParams.set("callback", CALLBACK_NAME);

    script.id = SCRIPT_ID;
    script.async = true;
    script.src = url.toString();
    document.head.appendChild(script);
  }).catch(error => {
    scriptPromise = null;
    loadedApiKey = null;
    throw error;
  });
  return scriptPromise;
}

export type GoogleMapsGeocodingLibrary = {
  Geocoder: new () => {
    geocode: (request: { location: GoogleMapsLatLngLiteral }) => Promise<{ results: Array<{
      place_id?: string; formatted_address?: string;
      address_components: Array<{ long_name: string; short_name: string; types: string[] }>;
    }> }>;
  };
};
export async function loadGoogleGeocodingLibrary(apiKey: string) {
  await loadGoogleMapsLibrary(apiKey);
  return (window as GoogleMapsWindow).google!.maps!.importLibrary!("geocoding") as Promise<GoogleMapsGeocodingLibrary>;
}
